import type * as CSS from 'csstype';
import _ from 'lodash';
import {v4 as uuidv4} from 'uuid';
import {
    AstNode,
    SymbolTable,
    EnrichedAstNode,
    EnrichedAstMetaData,
    AstView,
    AstPositionWithPadding,
    AstPositionWithMarginAndBorder,
} from '../types';
import {getGroupDimensions} from './GeometryHelpers';

// TODO remove other versions of this function.
export const roundNumber = (number: number | undefined) => {
    if (!number) {
        return 0;
    }
    if (number < 10) {
        return +number.toFixed(2);
    }
    if (number < 100) {
        return +number.toFixed(1);
    }
    return +number.toFixed(0);
};

const replaceAll = (input: string, search: string | RegExp, replaceWith: string) => {
    return input.split(search).join(replaceWith);
};

export const hasVisualContent = (node: EnrichedAstNode) => {
    if (node.isImported) {
        return true;
    }
    if (node.type === 'Image') {
        return true;
    }

    if (node.type === 'Text') {
        return true;
    }

    // backgroundColor
    if (node.styles['background'] && node.styles['background'] !== 'transparent') {
        return true;
    }
    if (node.styles['backgroundColor'] && node.styles['backgroundColor'] !== 'transparent') {
        return true;
    }
    if (node.styles['backgroundImage'] && node.styles['backgroundImage'] !== 'transparent') {
        return true;
    }
    if (node.properties['src']) {
        return true;
    }
    if (node.styles['borderColor'] && node.styles['borderColor'] !== 'transparent') {
        return true;
    }
    if (node.styles['filter'] || node.styles['backdropFilter'] || node.styles['boxShadow']) {
        return true;
    }
    return false;
};

export const getVisualChildren = (node: EnrichedAstNode) => {
    const visualChildren: EnrichedAstNode[] = [];

    node.children.forEach((child) => {
        if (hasVisualContent(child)) {
            visualChildren.push(child);
        } else {
            if (node.type !== 'Text') {
                visualChildren.push(...getVisualChildren(child));
            }
        }
    });

    return visualChildren;
};

export function camelize(str) {
    return str.replace(/(?:^\w|[A-Z]|\b\w|\s+)/g, function (match, index) {
        if (+match === 0) return ''; // or if (/\s+/.test(match)) for white spaces
        return index === 0 ? match.toLowerCase() : match.toUpperCase();
    });
}

function capitalize(str) {
    return str.charAt(0).toUpperCase() + str.slice(1);
}

const illegalNames = [
    'abstract',
    'arguments',
    'await',
    'boolean',
    'break',
    'byte',
    'case',
    'catch',
    'char',
    'class',
    'const',
    'continue',
    'debugger',
    'default',
    'delete',
    'do',
    'double',
    'else',
    'enum',
    'eval',
    'export',
    'extends',
    'false',
    'final',
    'finally',
    'float',
    'for',
    'function',
    'goto',
    'if',
    'implements',
    'import',
    'in',
    'instanceof',
    'int',
    'interface',
    'let',
    'long',
    'map',
    'native',
    'new',
    'null',
    'package',
    'private',
    'protected',
    'public',
    'return',
    'short',
    'static',
    'super',
    'switch',
    'synchronized',
    'this',
    'throw',
    'throws',
    'transient',
    'true',
    'try',
    'typeof',
    'var',
    'void',
    'volatile',
    'while',
    'with',
    'yield',
    'window',
    'array',
    'date',
    'eval',
    'function',
    'hasownproperty',
    'infinity',
    'isfinite',
    'isnan',
    'isprototypeof',
    'length',
    'math',
    'nan',
    'name',
    'number',
    'object',
    'prototype',
    'string',
    'tostring',
    'undefined',
    'valueof',
    'alert',
    'all',
    'anchor',
    'anchors',
    'area',
    'assign',
    'blur',
    'button',
    'checkbox',
    'clearinterval',
    'cleartimeout',
    'clientinformation',
    'close',
    'closed',
    'confirm',
    'constructor',
    'crypto',
    'decodeuri',
    'decodeuricomponent',
    'defaultstatus',
    'document',
    'element',
    'elements',
    'embed',
    'embeds',
    'encodeuri',
    'encodeuricomponent',
    'escape',
    'event',
    'fileupload',
    'focus',
    'form',
    'forms',
    'frame',
    'innerheight',
    'innerwidth',
    'layer',
    'layers',
    'link',
    'location',
    'mimetypes',
    'navigate',
    'navigator',
    'frames',
    'framerate',
    'hidden',
    'history',
    'image',
    'images',
    'offscreenbuffering',
    'open',
    'opener',
    'option',
    'outerheight',
    'outerwidth',
    'packages',
    'pagexoffset',
    'pageyoffset',
    'parent',
    'parsefloat',
    'parseint',
    'password',
    'pkcs11',
    'plugin',
    'prompt',
    'propertyisenum',
    'radio',
    'reset',
    'screenx',
    'screeny',
    'scroll',
    'secure',
    'select',
    'self',
    'setinterval',
    'settimeout',
    'status',
    'submit',
    'taint',
    'text',
    'textarea',
    'top',
    'unescape',
    'untaint',
    'window',
];

export function assignUniqueName(nodeId: string, str: string, symbolTable: SymbolTable, capitalizeOutput = true) {
    // remove old name from our DB.
    Object.keys(symbolTable.reservedSymbols).forEach((key) => {
        if (symbolTable.reservedSymbols[key] === nodeId) {
            delete symbolTable.reservedSymbols[key];
        }
    });

    // build new name.
    let i = 0;
    const numbers = replaceAll(str, /[0-9]/g, '');
    const questionMarks = replaceAll(numbers, '?', '');
    const underScore = replaceAll(questionMarks, '_', '');
    const exclamationMarks = replaceAll(underScore, '!', '');
    const camelCase = camelize(exclamationMarks.replace(/[^\w\s!?]/g, ''));
    let name = camelCase ? camelCase : 'Element';

    if (capitalizeOutput) {
        name = capitalize(name);
    }

    let outputName = name;
    // eslint-disable-next-line no-constant-condition
    while (true) {
        if (!symbolTable.reservedSymbols[outputName] && illegalNames.includes(outputName.toLowerCase()) === false) {
            break;
        }
        i++;
        outputName = `${name}${i}`;
    }

    symbolTable.reservedSymbols[outputName] = nodeId;

    return outputName;
}

function lookupBetterName(node: EnrichedAstNode, view: AstView) {
    const children = node.children.map((c) => c.internalId).sort();
    const result = view.meta.symbolTable.userNamesList.find((entry) => {
        return _.isEqual(entry.children, children);
    });
    if (result) {
        node.uniqueName = assignUniqueName(node.internalId, result.name, view.meta.symbolTable);
    }
}

export function recalculateSymbolTable(view: AstView) {
    view.meta.symbolTable.reservedSymbols = {};

    visitor(view.tree, view.meta, (node) => {
        view.meta.symbolTable.reservedSymbols[node.uniqueName] = node.internalId;
    });

    Object.values(view.meta.complexComponentMap).forEach((component) => {
        visitor(component.renderVariants.finalTree, view.meta, (node) => {
            view.meta.symbolTable.reservedSymbols[node.uniqueName] = node.internalId;
        });
    });
}

// TODO replace with visitorWithContext
export function visitor(
    ast: EnrichedAstNode,
    meta: EnrichedAstMetaData,
    fn: (ast: EnrichedAstNode, meta: EnrichedAstMetaData, parent?: EnrichedAstNode) => void,
    parent?: EnrichedAstNode
) {
    fn(ast, meta, parent);

    [...ast.children].forEach((child) => {
        visitor(child, meta, fn, ast);
    });
}

export async function visitorAsync(
    ast: EnrichedAstNode,
    meta: EnrichedAstMetaData,
    fn: (ast: EnrichedAstNode, meta: EnrichedAstMetaData, parent?: EnrichedAstNode) => Promise<void>,
    parent?: EnrichedAstNode
) {
    await fn(ast, meta, parent);
    const childCopy = [...ast.children];
    for (const child of childCopy) {
        await visitorAsync(child, meta, fn);
    }
}

export function visitorPostOrder(
    ast: EnrichedAstNode,
    meta: EnrichedAstMetaData,
    fn: (ast: EnrichedAstNode, meta: EnrichedAstMetaData, parent?: EnrichedAstNode) => void,
    parent?: EnrichedAstNode
) {
    [...ast.children].forEach((child) => {
        visitorPostOrder(child, meta, fn, ast);
    });

    fn(ast, meta, parent);
}

// the secret best function in the repo.
export function visitorWithContext<t>(
    ast: EnrichedAstNode,
    meta: EnrichedAstMetaData,
    fn: (ast: EnrichedAstNode, meta: EnrichedAstMetaData, context: t) => t,
    context: t
) {
    const contextForChildren = fn(ast, meta, context);

    [...ast.children].forEach((child) => {
        visitorWithContext(child, meta, fn, contextForChildren);
    });
}

export function forAllNodes(fn: (ast: EnrichedAstNode) => void) {
    return (ast: EnrichedAstNode, meta: EnrichedAstMetaData) => {
        visitor(ast, meta, fn);
    };
}

export function visitorEnriched(ast: EnrichedAstNode, fn: (ast: EnrichedAstNode) => void) {
    fn(ast);
    for (const child of ast.children) {
        visitorEnriched(child, fn);
    }
}

export async function visitorEnrichedAsync(ast: EnrichedAstNode, fn: (ast: EnrichedAstNode) => Promise<void>) {
    await fn(ast);
    for (const child of ast.children) {
        await visitorEnrichedAsync(child, fn);
    }
}

export function printTreeStructure(astRoot: AstNode) {
    let indent = 1;
    let result = '';
    const traverse = (ast: AstNode) => {
        result = result + '--' + Array(indent).join('--') + ast.name + '\n';
        indent++;
        ast.children.forEach(function (node) {
            traverse(node);
        });
        indent--;
    };

    traverse(astRoot);
}

const COMPARE_TOLERANCE_FIXED = 8;
/*
 * @param tolerance: 0.1 means 10% tolerance if percentage. 1 means 1px tolerance if fixed.
 */
export function isEqualWithTolerance(
    a: number,
    b: number,
    tolerance: number = COMPARE_TOLERANCE_FIXED,
    mode: 'fixed' | 'percent' = 'fixed'
) {
    const bigNumber = Math.max(a, b);
    const smallNumber = Math.min(a, b);
    if (mode === 'fixed') {
        return bigNumber - smallNumber < tolerance;
    } else {
        // percent
        return smallNumber / bigNumber > 1 - tolerance;
    }
}

const FIGMA_BLENDING_MODE_TO_BROWSER = {
    DARKEN: 'darken',
    MULTIPLY: 'multiply',
    SCREEN: 'screen',
    OVERLAY: 'overlay',
    LIGHTEN: 'lighten',
    COLOR_DODGE: 'color-dodge',
    COLOR_BURN: 'color-burn',
    HARD_LIGHT: 'hard-light',
    SOFT_LIGHT: 'soft-light',
    DIFFERENCE: 'difference',
    EXCLUSION: 'exclusion',
    HUE: 'hue',
    SATURATION: 'saturation',
    COLOR: 'color',
    LUMINOSITY: 'luminosity',
    NORMAL: 'normal',
};

export const figmaBlendingModeToCssBlendingMode = (figmaBlendMode: string): CSS.Property.MixBlendMode => {
    return FIGMA_BLENDING_MODE_TO_BROWSER[figmaBlendMode] || 'normal';
};

const DEFAULT_REM_CONVERSION = 16;

export const convertToREM = (attributeName, value) => {
    let result = value;
    const blackListRemProperties = ['backdropFilter', 'filter', 'flexGrow', 'fontWeight', 'boxShadow', '&'];

    if (blackListRemProperties.includes(attributeName)) {
        return result;
    }

    if (String(value).includes('url') || String(value).includes('var(--')) {
        result = value;
    } else if (typeof value === 'number') {
        result = roundNumber(+value / DEFAULT_REM_CONVERSION) + 'rem';
    } else if (value.includes('px')) {
        const convertMap = value.split('px').filter((value) => value !== '');
        result = convertMap.map((num) => roundNumber(+num / DEFAULT_REM_CONVERSION) + 'rem').join(' ');
    }

    return result;
};

export const isCssUnitZero = (attribute: string | number | undefined) => {
    const units = ['', '0', '0px', '0%', '0rem', '0em', '0vh', '0vw'];
    let result = false;

    if (attribute === undefined) {
        result = true;
    } else {
        for (const unit of units) {
            if (unit === attribute.toString().trim()) {
                result = true;
                break;
            }
        }
    }

    return result;
};

export const convertToPX = (attributeName: string, value: string | number) => {
    let result = value;
    const blackListRemProperties = ['backdropFilter', 'filter', 'flexGrow', 'fontWeight', 'boxShadow', '&'];

    if (blackListRemProperties.includes(attributeName)) {
        return result;
    }

    if (value.toString().includes('auto')) {
        return 'auto';
    }

    if (String(value).includes('url') || String(value).includes('var(--')) {
        result = value;
    } else if (typeof value === 'number') {
        result = roundNumber(+value) + 'px';
    } else if (value.includes('rem')) {
        const convertMap = value.split('rem').filter((value) => value !== '');
        result = convertMap.map((num) => +num * DEFAULT_REM_CONVERSION + 'px').join(' ');
    }

    return result;
};

export function recalculateInnerPosition(node: EnrichedAstNode) {
    const leftSize = node.innerPosition.paddingLeft + node.position.borderLeftWidth;

    const rightSize = node.innerPosition.paddingRight + node.position.borderRightWidth;

    const topSize = node.innerPosition.paddingTop + node.position.borderTopWidth;

    const bottomSize = node.innerPosition.paddingBottom + node.position.borderBottomWidth;

    const innerPosition: AstPositionWithPadding = {
        ...node.innerPosition,
        absoluteY: node.position.absoluteY + topSize,
        absoluteX: node.position.absoluteX + leftSize,
        width: node.position.width - (leftSize + rightSize),
        height: node.position.height - (topSize + bottomSize),
    };

    node.innerPosition = innerPosition;
}

export function groupNodeBuilder(children: EnrichedAstNode[], view: AstView, source: string, useChildIdAsId = false) {
    // should create a group with all inputs given.
    const {zIndex, position} = getGroupDimensions(children);

    const nodeChildren = children.sort((a, b) => a.zIndex - b.zIndex);
    const newId = createInternalId(nodeChildren, useChildIdAsId);
    // Should look up names from view.

    const newChildGroup: EnrichedAstNode = {
        name: 'group',
        visible: true,
        uniqueName: assignUniqueName(newId, 'group', view.meta.symbolTable),
        children: nodeChildren,

        type: 'Frame',
        styles: {
            alignSelf: 'stretch',
            width: position.width,
            height: position.height,
        },

        position: {...getBasePositionWithMarginAndBorder(), ...position},
        innerPosition: {...getBasePositionWithPadding(), ...position},
        zIndex,
        meta: {
            systemGenerated: true,
            elementDetections: {},
            source: source,
        },
        isImported: false,
        properties: {},
        mandatoryProperties: [],
        htmlTag: 'div',
        internalId: newId,
        calculatedLayoutAbstraction: {
            units: {},
            maxWidth: false,
            flexDirection: 'row',
            justifyContent: 'flex-start',
            borderBox: 'content-box',
            alignSelf: 'flex-start',
            widthSpacingStrategy: 'hug',
            heightSpacingStrategy: 'hug',
            height: undefined,
            width: undefined,
            absolutePosition: false,
        },
        inputLayoutAbstraction: {},
        systemLayoutAbstraction: {},
        userInput: {},
        backgroundHandler: {
            childToParentBlend: 'normal',
            containsVisualInfo: false,
            pluginExportType: 'figma',
            supported: true,

            fills: [],
        },
    };
    lookupBetterName(newChildGroup, view);
    return newChildGroup;
}

const createInternalId = (children: EnrichedAstNode[], useChildIdAsId: boolean) => {
    if (children.length === 0 || !useChildIdAsId) {
        return uuidv4();
    }
    // TODO maybe short then IDs here.
    return children
        .map((c) => c.internalId)
        .sort()
        .join();
};

export const shouldRename = (tree: EnrichedAstNode) => {
    // remove number
    // check if vector, group, frame, rectangle, vector

    const defaultNames = ['Frame', 'Rectangle', 'Vector', 'Group'];

    if (defaultNames.some((name) => tree.uniqueName.includes(name))) {
        return true;
    }

    if (tree.uniqueName.length > 20) {
        return true;
    }

    if (tree.type === 'Text' && tree.text === tree.name) {
        return true;
    }

    return false;
};

export const getBasePositionWithMarginAndBorder = (): AstPositionWithMarginAndBorder => ({
    absoluteX: 0,
    absoluteY: 0,
    marginTop: 0,
    marginRight: 0,
    marginBottom: 0,
    marginLeft: 0,
    borderLeftWidth: 0,
    borderRightWidth: 0,
    borderTopWidth: 0,
    borderBottomWidth: 0,
    width: 0,
    height: 0,
});

export const getBasePositionWithPadding = (): AstPositionWithPadding => ({
    absoluteX: 0,
    absoluteY: 0,
    paddingTop: 0,
    paddingRight: 0,
    paddingBottom: 0,
    paddingLeft: 0,
    width: 0,
    height: 0,
    gap: 0,
});

export const getBaseCalculatedLayoutAbstraction = (): EnrichedAstNode['calculatedLayoutAbstraction'] => ({
    maxWidth: false,
    units: {},
    flexDirection: 'row',
    justifyContent: 'flex-start',
    alignSelf: 'flex-start',
    borderBox: 'content-box',
    widthSpacingStrategy: 'hug',
    heightSpacingStrategy: 'hug',
    height: undefined,
    width: undefined,
    absolutePosition: false,
});
